import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';

import enrollmentRxLogo from 'assets/EnrollmentRxLogo.svg';
import fullFabricLogo from 'assets/FullFabricLogo.svg';
import liaisonLogo from 'assets/LiaisonLogo.svg';
import salesforceLogo from 'assets/SalesforceLogo.svg';
import slateLogo from 'assets/SlateLogo.svg';
import zapierLogo from 'assets/ZapierLogo.svg';
import Button, { ButtonVariant } from 'components/Button/Button';
import ContentWrapper from 'components/ContentWrapper/ContentWrapper';
import WelcomeBanner, {
  WelcomeBannerVariant,
} from 'components/WelcomeBanner/WelcomeBanner';
import useGetAssessmentDetails from 'hooks/useGetAssessmentDetails';
import { t } from 'locales/i18n';

import IntegrationCard from './IntegrationCard';

const Integrations = () => {
  const [value, setValue] = useState('');
  const { interviewUid } = useParams();
  const { data } = useGetAssessmentDetails(interviewUid ?? '');

  const assessmentName = data?.name;

  useEffect(() => {
    if (assessmentName === undefined || value === '') {
      return;
    }
    const message =
      value !== 'custom'
        ? `Hello! I'd like to know more about how to enable an integration with ${value} for the assessment ${assessmentName}.`
        : `Hello! I'm interested in setting up an integration for ${assessmentName} that is not on your standard list.`;
    window.Intercom('showNewMessage', message);
  }, [value]);

  return (
    <ContentWrapper title={t('Integrations')}>
      <div className="my-6 grid grid-cols-2 gap-6 2xl:grid-cols-3">
        <IntegrationCard
          title="AMP"
          website="paperlessadmissions.com"
          description={t(
            "AMP supports an integration with Kira's APIv2 that enables reviewer scores to be pulled from Kira into AMP on a daily basis."
          )}
          logo={zapierLogo}
          backgroundColor="bg-[#D4E5631A]"
          setValue={setValue}
        />
        <IntegrationCard
          title="Enrollment Rx"
          website="enrollmentrx.com"
          description={t(
            "Enrollment Rx supports an integration with Kira's APIv2. When enabled, the integration allows applicants to be automatically invited to complete Kira. Additionally, a webhook notification can be sent when applicants complete their assessment that includes a link to the Kira review page."
          )}
          logo={enrollmentRxLogo}
          backgroundColor="bg-[#9411111A]"
          setValue={setValue}
        />
        <IntegrationCard
          title="Full Fabric"
          website="fullfabric.com"
          description={t(
            "Full Fabric supports an integration with Kira's APIv2. When enabled, the integration allows applicants to be automatically invited to complete Kira. Additionally, a webhook notification can be sent when applicants complete their assessment that includes a link to the Kira review page."
          )}
          logo={fullFabricLogo}
          backgroundColor="bg-[#86868B1A]"
          setValue={setValue}
        />
        <IntegrationCard
          title="Liaison CAS"
          website="liaisonedu.com"
          description={t(
            'Liaison CAS supports an integration with a number of CAS verticals (if you have a question about whether your vertical is supported please contact us below). Once enabled, the integration allows applicants to be automatically invited to complete Kira via their CAS application, and a webhook notification is sent when applicants complete their assessment that includes a link to the Kira review page.'
          )}
          logo={liaisonLogo}
          backgroundColor="bg-[#B5BE341A]"
          setValue={setValue}
        />
        <IntegrationCard
          title="Salesforce"
          website="salesforce.com"
          description={t(
            "An integration with Salesforce can be set up with support from a developer or external consultant working with your school. Any functionality available via Kira's APIv2 can be utilized."
          )}
          logo={salesforceLogo}
          backgroundColor="bg-[#00A1E01A]"
          setValue={setValue}
        />
        <IntegrationCard
          title="Slate"
          website="technolutions.com"
          description={t(
            "Slate supports an integration with Kira's APIv2 for all schools using their online application system. Once enabled, the integration allows applicants to be automatically invited to complete Kira via Slate, and review links can be automatically pulled back into the applicant's Slate record based on an interval (e.g. hourly, daily)."
          )}
          logo={slateLogo}
          backgroundColor="bg-[#00669E1A]"
          setValue={setValue}
        />
      </div>
      <WelcomeBanner
        title={t("Can't find what you're looking for?")}
        description={t(
          'Please contact our Client Success team for additional information about integrations.'
        )}
        variant={WelcomeBannerVariant.INTEGRATION}
      >
        <Button
          variant={ButtonVariant.SOLID}
          buttonText={t('Contact Kira')}
          buttonClassNames="!mt-4"
          onClick={() => setValue('custom')}
        />
      </WelcomeBanner>
    </ContentWrapper>
  );
};

export default Integrations;
